import './scripts/modernizr';
import 'intersection-observer';
import 'bootstrap';

import $ from 'jquery';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Lazyload from 'vanilla-lazyload';

import initHeroBlock from '@blocks/hero';
import initPlanCompareBlock from '@blocks/plan-compare';
import initStatsBlock from '@blocks/stats';
import initLogosBlock from '@blocks/logos';

import initTextMediaTabsBlock from '@blocks/text-media-tabs';

import './blocks/tabs/frontend';

import { gsap } from "gsap";

import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const toKebab = (string:string): string => {
  return string
    .split('')
    .map((letter, index) => {
      if (/[A-Z]/.test(letter)) {
        return ` ${letter.toLowerCase()}`
      }
      return letter
    })
    .join('')
    .trim()
    .replace(/[_\s]+/g, '-')
}

const toTitle = (string:string): string => {
  return toKebab(string)
    .split('-')
    .map(word => {
      return word.slice(0, 1).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

const properCase = (s: string): string => s.split(' ').map(s => s[0].toUpperCase() + s.substring(1, s.length)).join(' ')


declare global {
    interface Window {
        gmak: string; // gmak - Google Maps API Key
        initGoogleMaps: () => void;
        Sentry?: {
            captureException: (e: Error) => void;
        };
    }
}

const supportPageOffset = window.pageXOffset !== undefined;
const isCSS1Compat = ((document.compatMode || "") === "CSS1Compat");

const getScrollPosition = (): { scrollX: number; scrollY: number } => {
    const x = supportPageOffset ? window.pageXOffset : isCSS1Compat ? document.documentElement.scrollLeft : document.body.scrollLeft;
    const y = supportPageOffset ? window.pageYOffset : isCSS1Compat ? document.documentElement.scrollTop : document.body.scrollTop;
    return { scrollX: x, scrollY: y };
}

const initLazyLoad = (): void => {
    new Lazyload({
        // eslint-disable-next-line
        elements_selector: '[data-bg], [data-src]',
        // eslint-disable-next-line
        callback_loaded: (el: HTMLElement) => {
            if (el.parentElement.classList.contains('hero')) {
                el.parentElement.classList.add('hero--loaded');
            }
        },
    })
}


const initParallax = (): void => {
    const maxTranslation = 300;
    const items = document.querySelectorAll('.parallax');
    const adjust = (item: HTMLElement): void => {
        const { scrollY } = getScrollPosition();
        let translation = Math.floor((scrollY - item.parentElement.offsetTop) / 4);

        if (translation > maxTranslation) {
            translation = maxTranslation;
        }

        if (translation < -maxTranslation) {
            translation = -maxTranslation;
        }

        item.style.transform = `translate3d(0, ${translation}px, 0)`;
    }

    if (items && items.length) {
        items.forEach((item: HTMLElement) => adjust(item));
    }

    document.addEventListener('scroll', () => {
        if (items && items.length) {
            items.forEach((item: HTMLElement) => adjust(item))
        }
    }, window.Modernizr.passiveeventlisteners ? { passive: true } : false)
}

const initDynamicHeader = (): void => {
    const header = document.getElementById('header');
    const setHeaderScrolled = (): void => {
        const { scrollY } = getScrollPosition();
        if (scrollY > 50) {
            header.classList.add('header--scrolled');
        } else {
            header.classList.remove('header--scrolled');
        }
    }

    document.addEventListener('scroll', () => {
        setHeaderScrolled();
    }, window.Modernizr.passiveeventlisteners ? { passive: true } : false);
}

const initGoogleMaps = (): void => {
    console.log('Initialize Google Maps...');
}

let mobileMenuOpen = false;
let openMenu: () => void;
let closeMenu: () => void;
const initMobileMenuToggle = (): void => {
    // init mobile menu toggle
    //
    const header = document.getElementById('header');
    const toggle = document.getElementById('mobile-nav-toggle');
    const navigation = document.getElementById('header__mobile-nav');

    openMenu = (): void => {
        // open the menu
        disableBodyScroll(navigation);
        toggle.classList.add('is-active');
        header.classList.add('header--mobile-nav-open');
        navigation.classList.add('header__mobile-nav--open');
        mobileMenuOpen = true;
    };

    closeMenu = (): void => {
        // close the menu
        enableBodyScroll(navigation);
        toggle.classList.remove('is-active');
        header.classList.remove('header--mobile-nav-open');
        navigation.classList.remove('header__mobile-nav--open');
        mobileMenuOpen = false;
    };

    toggle && toggle.addEventListener('click', () => {
        if (mobileMenuOpen) {
            closeMenu();
        } else {
            openMenu();
        }
    });

    const sections = document.querySelectorAll('#header__mobile-nav a');
    sections && sections.forEach((a: HTMLElement) => {
        a.addEventListener('click', (e) => {
            if (!e.defaultPrevented) {
                typeof closeMenu === 'function' && closeMenu();
            }
        });
    });
}


const initScrollTriggers = (): void => {
    const main = document.getElementById('site-content');
    const selectors = [
        '.sm-element',
        '.wp-block-media-text__media',
        '.wp-block-media-text__content',
        'h1.has-text-align-center',
        'h2.has-text-align-center',
        'h3.has-text-align-center',
        'h4.has-text-align-center',
        'h5.has-text-align-center',
        'h6.has-text-align-center',
    ]

    const elements = main.querySelectorAll(selectors.join(', '));

    if (elements) {
        elements.forEach((element: HTMLElement) => {
            ScrollTrigger.create({
                trigger: element,
                start: 'top bottom+=200px',
                toggleClass: 'sm-visible',
            });
        })
    }
}

const initFooterMenus = (): void => {
    var isMobile = false; //initiate as false
    // device detection
    if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) 
        || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0,4))) { 
        isMobile = true;
    }

    if(isMobile === true){
        const menus = document.querySelectorAll('.footer__widget-2 div[class^="menu"][class$="container"]');
        menus.forEach((menu: HTMLElement) => {
        menu.setAttribute('aria-haspopup', 'true');
        menu.setAttribute('aria-expanded', 'false');
        menu.addEventListener('click', () => {
            if (menu.getAttribute('aria-expanded') === 'true') {
                menu.setAttribute('aria-expanded', 'false');
                menu.style.height = '0px';
            } else {
                menu.setAttribute('aria-expanded', 'true');
                const height = menu.getElementsByClassName('menu')[0].getBoundingClientRect().height;
                menu.style.height = `${height}px`;
            }
        })
    })
    }
}

const initFixedNav = (): void => {
    const group = document.querySelectorAll('.fixed-nav');

    const menu = document.createElement('div');
    menu.setAttribute('role', 'nav');

    group.forEach((group: HTMLElement) => {
        const container = document.createElement('div');
        container.classList.add('fixed-nav__positioning');
        const sections = group.querySelectorAll('[id]');
        group.style.position = 'relative';

        const listItems = Array.from(sections).map((section, i) => {
            const inactiveColor = section.getAttribute('data-inactive-color');
            const activeColor = section.getAttribute('data-active-color');

            return `
                <li data-section-id="${section.id}" data-inactive-color="${inactiveColor}" data-active-color="${activeColor}" class="fixed-nav__list-item">
                    <a href="#${section.id}" data-count="${String(i + 1).padStart(2, '0')}">${toTitle(section.id)}</a>
                </li>
            `
        }).join('\n')

        container.innerHTML = `
            <ul class="fixed-nav__list" role="navigation" aria-label="Fixed Navigation">
                <h3 class="fixed-nav__title">${toTitle(group.getAttribute('id'))}</h3>
                ${listItems}
            </ul>
        `;

        group.appendChild(container);

        ScrollTrigger.create({
            trigger: group,
            start: 'top +=200px',
            end: 'bottom +=600px',
            toggleClass: {
                targets: container,
                className: 'active',
            },
        })

        sections.forEach((section: HTMLElement) => {
            const inactiveColor = section.getAttribute('data-inactive-color');
            const activeColor = section.getAttribute('data-active-color');

            ScrollTrigger.create({
                trigger: section,
                start: 'top +=200px',
                end: 'bottom +=200px',
                // markers: true,
                // pin: true,
                // snap: 1,
                // pinSpacing: false,
                anticipatePin: 1,
                toggleClass: {
                    targets: `[data-section-id="${section.id}"]`,
                    className: 'active',
                },
                onEnter: () => {
                    group.querySelectorAll('[data-section-id]').forEach((el: HTMLElement) => {
                        if (el.getAttribute('data-section-id') === section.id) {
                            el.style.color = activeColor;
                        } else {
                            el.style.color = inactiveColor;
                        }
                    });

                    group.querySelectorAll('h3').forEach((el: HTMLElement) => {
                        el.style.color = activeColor;
                    });
                },
                onEnterBack: () => {
                    group.querySelectorAll('[data-section-id]').forEach((el: HTMLElement) => {
                        if (el.getAttribute('data-section-id') === section.id) {
                            el.style.color = activeColor;
                        } else {
                            el.style.color = inactiveColor;
                        }
                    });

                    group.querySelectorAll('h3').forEach((el: HTMLElement) => {
                        el.style.color = activeColor;
                    });
                }
            })
        });
    });
}

const initSmoothScroll = (): void => {
    // Select all links with hashes
    $('a[href*=\\#]:not([href=\\#])').on('click', function (e) {
        e.preventDefault();

        // Abort if element has a data-toggle attribute, tabs use data-toggle too so removed the tabs conditional
        const $dataToggle = $(this).attr('data-toggle');
        if (typeof ($dataToggle) != "undefined" && $dataToggle.length > 0) return;

        // @ts-ignore
        if (location.pathname.replace(/^\//, '') == this.pathname.replace(/^\//, '') || location.hostname == this.hostname) {

            // @ts-ignore
            const hash = this.hash,
                target = $(hash);

            // @ts-ignore
            target = target.length ? target : $('[name=' + this.hash.slice(1) + ']');
            if (target.length) {
                $('html,body').animate({
                    scrollTop: target.offset().top
                }, 500, function () {
                    location.hash = hash;
                    target.focus();
                    if (target.is(":focus")) { //checking if the target was focused
                        return false;
                    } else {
                        target.attr('tabindex', '-1'); //Adding tabindex for elements not focusable
                        target.focus(); //Setting focus
                    }
                });
            }
        }
    });
}

const initActiveHeaderMenu = (): void => {
    document.querySelectorAll('.menu a[href*=\\#]:not([href=\\#])').forEach((anchor: Element) => {
        ScrollTrigger.create({
            trigger: anchor.getAttribute('href'),
            start: 'top +=200px',
            end: 'bottom +=200px',
            anticipatePin: 1,
            toggleClass: {
                className: 'active',
                targets: anchor,
            },
        });
    })
}

$(document).ready(function () {
    [
        initHeroBlock,
        initPlanCompareBlock,
        initLazyLoad,
        initParallax,
        initMobileMenuToggle,
        initScrollTriggers,
        initDynamicHeader,
        initFooterMenus,
        initStatsBlock,
        initFixedNav,
        initLogosBlock,
        initSmoothScroll,
        initTextMediaTabsBlock,
        initActiveHeaderMenu,
    ].forEach(fn => {
        try {
            fn();
        } catch (e) {
            console.error(e);
            try {
                if (typeof window.Sentry === 'object' && typeof window.Sentry.captureException === 'function') {
                    window.Sentry.captureException(e);
                }
            } catch (_e) {
                console.error('Failed to send error to Sentry')
                console.error(_e);
            }
        }
    })


    if (window.gmak) {
        window.initGoogleMaps = initGoogleMaps;
        const s = document.createElement('script');
        s.async = true;
        s.defer = true;
        s.src = `https://maps.googleapis.com/maps/api/js?key=${window.gmak}&callback=initGoogleMaps`;
        document.body.appendChild(s);
    }

    $('.banner__closebtn').on( 'click', function(){
        $( '.banner__container' ).slideToggle();
    });
});
